/**
 * Game-centre identity — name + logo shown on every screen and on the PDF
 * summary, plus the winner-banner message template shown on Final Results.
 * Purely cosmetic: never touches scoring, never snapshotted per game (unlike
 * house rules), just read fresh from localStorage on render.
 */

import { esc } from './helpers';

export interface Branding {
  name: string;
  /** A data: URL — logos are stored inline since there is no backend to upload to. */
  logoDataUrl?: string;
  /**
   * Free text for the Final Results winner banner (see `src/ui/views/
   * summary.ts`/`guestPlay.ts`'s `formatWinnerBannerText`), with `{winner}`
   * as a placeholder for the winner's name(s) — insertable anywhere in the
   * text, not just a fixed prefix, so an admin can write their own remark
   * around it. Optional: `{winner}` can be omitted entirely for a static
   * message with no name inserted.
   */
  winnerMessage?: string;
}

const BRANDING_KEY = 'mahjong-scorer:branding';

/** The literal default banner text — exported so a guest's mirrored branding (which may predate this field) has the same fallback to reach for. */
export const DEFAULT_WINNER_MESSAGE = '🎉 Congrats, {winner}! 🏆';

export const DEFAULT_BRANDING: Branding = {
  name: 'Your Game Centre',
  winnerMessage: DEFAULT_WINNER_MESSAGE,
};

export function readBranding(): Branding {
  const stored = localStorage.getItem(BRANDING_KEY);
  if (!stored) return DEFAULT_BRANDING;
  try {
    return { ...DEFAULT_BRANDING, ...(JSON.parse(stored) as Partial<Branding>) };
  } catch {
    return DEFAULT_BRANDING;
  }
}

export function writeBranding(branding: Branding): void {
  localStorage.setItem(BRANDING_KEY, JSON.stringify(branding));
}

/**
 * The header every screen (and the printed PDF) shows at the top. Pass
 * `override` on a guest device (see `src/ui/views/guestPlay.ts`) — it has no
 * branding of its own in localStorage, so it renders whatever the main
 * device's branding was mirrored into the shared game state instead of
 * silently falling back to the generic default.
 */
export function renderBrandHeader(override?: Branding): string {
  const { name, logoDataUrl } = override ?? readBranding();
  const logo = logoDataUrl
    ? `<img src="${logoDataUrl}" alt="" class="brand-logo" />`
    : `<span class="brand-logo brand-logo-placeholder" aria-hidden="true">🀄</span>`;
  return `
    <header class="brand-header">
      ${logo}
      <span class="brand-name">${esc(name)}</span>
      <button type="button" class="theme-picker-btn" aria-label="Choose a look and text size" title="Look and text size">🎨</button>
    </header>
  `;
}
